import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Crown, Check, Zap, Bell, Filter, Users, ArrowRight, Map, Calculator, Download, Star, Sparkles, Bot, ExternalLink } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COUNCILLOR_WATCH_URL = 'https://poe.com/Councillor-Watch';

const TIERS = {
  free: {
    name: 'Free',
    description: 'Get started with the basics',
    price: '£0',
    interval: 'forever',
    articles: '3 articles/day',
    features: [
      'Access to news feed',
      'Basic elections data',
      'View-only maps',
      'Poll tracker',
    ],
    notIncluded: [
      'Postcode MP search',
      'What If calculator',
      'Email alerts',
      'Ad-free experience',
    ],
    color: 'slate',
    icon: Users,
  },
  councillor_watch: {
    name: 'Councillor Watch',
    description: 'AI-powered political search',
    monthlyPrice: '£5',
    articles: 'N/A',
    features: [
      'Search all 650 MPs',
      'Search ~20,000 councillors',
      'Research mayors & PCCs',
      'Full council breakdowns',
      'Build research dossiers',
      'AI-powered scanning',
      'Track elections & candidates',
      'Personal watchlist',
    ],
    notIncluded: [],
    color: 'cyan',
    icon: Bot,
    external: true,
  },
  enhanced: {
    name: 'Enhanced',
    description: 'Full access to core features',
    monthlyPrice: '£3.99',
    yearlyPrice: '£39.99',
    articles: '15 articles/day',
    features: [
      'Everything in Free, plus:',
      '15 articles per day',
      'Postcode MP search',
      'What If seat calculator',
      'Email alerts (1 party)',
      'Ad-free experience',
    ],
    notIncluded: [
      'Unlimited articles',
      'All party alerts',
      'Councillor Watch AI',
    ],
    color: 'amber',
    icon: Star,
    popular: true,
  },
  ultra: {
    name: 'Ultra',
    description: 'Everything, unlimited',
    monthlyPrice: '£7.99',
    yearlyPrice: '£79.99',
    articles: 'Unlimited',
    features: [
      'Everything in Enhanced, plus:',
      'Unlimited articles',
      'Email alerts (ALL parties)',
      'Priority breaking news',
      'Local council news feeds',
      'Export data & reports',
      'API access',
      'Priority support',
    ],
    notIncluded: [],
    color: 'purple',
    icon: Sparkles,
    yearlyBonus: 'Councillor Watch AI FREE',
  }
};

export const SubscribePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();
  const { isPremium, subscription, refreshSubscription } = useSubscription();
  const [loading, setLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [selectedTier, setSelectedTier] = useState('ultra');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/subscription/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.payment_status === 'paid') {
        toast.success('Welcome! Your subscription is now active.');
        refreshSubscription();
        navigate('/subscribe', { replace: true });
      }
    } catch (error) {
      console.error('Failed to check payment status:', error);
    }
  };

  const handleSubscribe = async (tier) => {
    if (tier === 'free') {
      navigate('/');
      return;
    }

    if (tier === 'councillor_watch') {
      // Redirect to Stripe checkout for Councillor Watch
      if (!user) {
        navigate('/login?redirect=/subscribe');
        return;
      }
      
      setLoading(true);
      setSelectedTier(tier);
      
      try {
        const response = await axios.post(`${API}/subscription/checkout`, {
          package_id: 'councillor_watch_monthly',
          origin_url: window.location.origin
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        window.location.href = response.data.checkout_url;
      } catch (error) {
        console.error('Checkout error:', error);
        toast.error('Failed to start checkout. Please try again.');
        setLoading(false);
      }
      return;
    }

    if (!user) {
      navigate('/login?redirect=/subscribe');
      return;
    }

    const packageId = `${tier}_${billingCycle}`;
    
    setLoading(true);
    setSelectedTier(tier);
    
    try {
      const response = await axios.post(`${API}/subscription/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  // Check if user has Councillor Watch access (Ultra yearly subscribers)
  const hasCouncillorWatchAccess = subscription?.package?.includes('ultra_yearly') || 
    subscription?.package?.includes('councillor_watch');

  // If already premium, show status
  if (isPremium) {
    const tierName = subscription?.tier === 'ultra' ? 'Ultra' : 'Enhanced';
    const TierIcon = subscription?.tier === 'ultra' ? Sparkles : Star;
    
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 pb-28">
        <div className="max-w-md w-full text-center">
          <div className={`bg-gradient-to-br ${subscription?.tier === 'ultra' ? 'from-purple-500 to-purple-700' : 'from-amber-400 to-amber-600'} p-4 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center`}>
            <TierIcon className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-3xl font-black mb-2">You're on {tierName}!</h1>
          <p className="text-slate-600 mb-6">
            Your subscription is active until{' '}
            <strong>{new Date(subscription?.expires_at).toLocaleDateString('en-GB', { 
              day: 'numeric', month: 'long', year: 'numeric' 
            })}</strong>
          </p>
          
          {/* Councillor Watch Access Card */}
          {hasCouncillorWatchAccess && (
            <div className="bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl p-5 mb-6 text-white text-left">
              <div className="flex items-center gap-3 mb-3">
                <Bot className="h-6 w-6" />
                <span className="font-bold">Councillor Watch AI</span>
                <span className="bg-white/20 text-xs px-2 py-0.5 rounded-full">INCLUDED</span>
              </div>
              <p className="text-sm text-white/90 mb-4">
                AI-powered UK political intelligence — search MPs, councillors, mayors, and more.
              </p>
              <a 
                href={COUNCILLOR_WATCH_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-cyan-600 font-bold px-4 py-2 rounded-lg hover:bg-cyan-50 transition-colors"
              >
                Launch Councillor Watch
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          )}
          
          {subscription?.tier !== 'ultra' && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
              <p className="text-purple-800 text-sm font-medium">
                Upgrade to Ultra Yearly for unlimited articles, all party alerts, API access, 
                <span className="font-bold"> + Councillor Watch AI FREE!</span>
              </p>
              <Button 
                onClick={() => handleSubscribe('ultra')}
                className="mt-3 bg-purple-600 hover:bg-purple-700"
              >
                Upgrade to Ultra
              </Button>
            </div>
          )}
          
          <div className="space-y-3">
            <Button onClick={() => navigate('/settings')} className="w-full" variant="outline">
              Manage Preferences
            </Button>
            <Button onClick={() => navigate('/referral')} variant="ghost" className="w-full">
              Refer Friends & Earn Free Months
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Calculate days left in promo
  const getDaysLeftInMonth = () => {
    const now = new Date();
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return Math.ceil((endOfMonth - now) / (1000 * 60 * 60 * 24));
  };
  const daysLeft = getDaysLeftInMonth();
  const promoActive = daysLeft > 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12 px-4 pb-28">
      <div className="max-w-7xl mx-auto">
        {/* Promo Banner */}
        {promoActive && (
          <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white rounded-2xl p-6 mb-10 text-center relative overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -top-4 -left-4 w-32 h-32 bg-white/10 rounded-full blur-3xl" />
              <div className="absolute -bottom-4 -right-4 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
            </div>
            <div className="relative">
              <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-1 rounded-full text-sm font-medium mb-3">
                <Sparkles className="h-4 w-4" />
                Limited Time Offer
              </div>
              <h2 className="text-3xl md:text-4xl font-black mb-2">50% OFF All Plans!</h2>
              <p className="text-white/90 mb-2">Use code <span className="font-mono bg-white/20 px-2 py-0.5 rounded">LAUNCH50</span> or it's applied automatically</p>
              <p className="text-white/80 text-sm">{daysLeft} day{daysLeft !== 1 ? 's' : ''} left - Offer ends at midnight on the last day of this month</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-black mb-4">Choose Your Plan</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Access exclusive political intelligence, from breaking scandals to AI-powered research
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-10">
          <div className="bg-slate-100 p-1 rounded-full inline-flex">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-full font-medium transition-all ${
                billingCycle === 'monthly' 
                  ? 'bg-white shadow text-slate-900' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2 rounded-full font-medium transition-all ${
                billingCycle === 'yearly' 
                  ? 'bg-white shadow text-slate-900' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Yearly <span className="text-green-600 text-sm ml-1">Save 17%</span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {/* Free Tier */}
          <div className="relative rounded-2xl border-2 border-slate-200 bg-white p-6 flex flex-col">
            <div className="mb-6">
              <div className="bg-slate-100 p-3 rounded-xl w-fit mb-4">
                <Users className="h-6 w-6 text-slate-600" />
              </div>
              <h3 className="text-2xl font-bold">Free</h3>
              <p className="text-slate-500 text-sm">Get started with the basics</p>
            </div>
            
            <div className="mb-6">
              <p className="text-4xl font-black">£0</p>
              <p className="text-slate-500">forever</p>
            </div>
            
            <div className="mb-6 flex-1">
              <p className="font-semibold text-sm text-slate-700 mb-3">3 articles per day</p>
              <ul className="space-y-2">
                {TIERS.free.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => navigate('/')}
              data-testid="btn-free"
            >
              Continue Free
            </Button>
          </div>

          {/* Councillor Watch */}
          <div className="relative rounded-2xl border-2 border-cyan-400 bg-gradient-to-b from-cyan-50 to-white p-6 flex flex-col">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-cyan-500 text-white text-xs font-bold px-3 py-1 rounded-full">
              AI POWERED
            </div>
            
            <div className="mb-6">
              <div className="bg-cyan-100 p-3 rounded-xl w-fit mb-4">
                <Bot className="h-6 w-6 text-cyan-700" />
              </div>
              <h3 className="text-2xl font-bold">Councillor Watch</h3>
              <p className="text-slate-600 text-sm">AI political intelligence</p>
            </div>
            
            <div className="mb-6">
              <p className="text-4xl font-black">£5</p>
              <p className="text-slate-600">/month</p>
            </div>
            
            <div className="mb-6 flex-1">
              <p className="font-semibold text-sm text-cyan-800 mb-3">Standalone AI tool</p>
              <ul className="space-y-2">
                {TIERS.councillor_watch.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <Button 
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-white"
              onClick={() => handleSubscribe('councillor_watch')}
              disabled={loading && selectedTier === 'councillor_watch'}
              data-testid="btn-councillor-watch"
            >
              {loading && selectedTier === 'councillor_watch' ? 'Processing...' : 'Get Councillor Watch'}
            </Button>
          </div>

          {/* Enhanced Tier */}
          <div className="relative rounded-2xl border-2 border-amber-400 bg-amber-50 p-6 flex flex-col shadow-lg">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">
              MOST POPULAR
            </div>
            
            <div className="mb-6">
              <div className="bg-amber-200 p-3 rounded-xl w-fit mb-4">
                <Star className="h-6 w-6 text-amber-700" />
              </div>
              <h3 className="text-2xl font-bold">Enhanced</h3>
              <p className="text-slate-600 text-sm">Full access to core features</p>
            </div>
            
            <div className="mb-6">
              <p className="text-4xl font-black">
                {billingCycle === 'monthly' ? '£3.99' : '£39.99'}
              </p>
              <p className="text-slate-600">
                {billingCycle === 'monthly' ? '/month' : '/year'}
                {billingCycle === 'yearly' && <span className="text-green-600 ml-2">= £3.33/mo</span>}
              </p>
            </div>
            
            <div className="mb-6 flex-1">
              <p className="font-semibold text-sm text-amber-800 mb-3">15 articles per day</p>
              <ul className="space-y-2">
                {TIERS.enhanced.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <Button 
              className="w-full bg-amber-500 hover:bg-amber-600 text-white"
              onClick={() => handleSubscribe('enhanced')}
              disabled={loading && selectedTier === 'enhanced'}
              data-testid="btn-enhanced"
            >
              {loading && selectedTier === 'enhanced' ? 'Processing...' : 'Get Enhanced'}
            </Button>
          </div>

          {/* Ultra Tier */}
          <div className="relative rounded-2xl border-2 border-purple-400 bg-gradient-to-b from-purple-50 to-white p-6 flex flex-col">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-600 to-cyan-500 text-white text-xs font-bold px-3 py-1 rounded-full">
              {billingCycle === 'yearly' ? 'BEST VALUE + FREE AI' : 'BEST VALUE'}
            </div>
            
            <div className="mb-6">
              <div className="bg-purple-200 p-3 rounded-xl w-fit mb-4">
                <Sparkles className="h-6 w-6 text-purple-700" />
              </div>
              <h3 className="text-2xl font-bold">Ultra</h3>
              <p className="text-slate-600 text-sm">Everything, unlimited</p>
            </div>
            
            <div className="mb-6">
              <p className="text-4xl font-black">
                {billingCycle === 'monthly' ? '£7.99' : '£79.99'}
              </p>
              <p className="text-slate-600">
                {billingCycle === 'monthly' ? '/month' : '/year'}
                {billingCycle === 'yearly' && <span className="text-green-600 ml-2">= £6.67/mo</span>}
              </p>
            </div>
            
            {/* Councillor Watch Bonus Banner */}
            {billingCycle === 'yearly' && (
              <div className="mb-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg p-3 flex items-center gap-2">
                <Bot className="h-5 w-5 flex-shrink-0" />
                <div className="text-sm">
                  <span className="font-bold">Councillor Watch AI</span>
                  <span className="text-white/90"> included FREE!</span>
                </div>
              </div>
            )}
            
            <div className="mb-6 flex-1">
              <p className="font-semibold text-sm text-purple-800 mb-3">Unlimited articles</p>
              <ul className="space-y-2">
                {TIERS.ultra.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="font-medium">{feature}</span>
                  </li>
                ))}
                {billingCycle === 'yearly' && (
                  <li className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 text-cyan-500 mt-0.5 flex-shrink-0" />
                    <span className="font-bold text-cyan-600">Councillor Watch AI</span>
                  </li>
                )}
              </ul>
            </div>
            
            <Button 
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              onClick={() => handleSubscribe('ultra')}
              disabled={loading && selectedTier === 'ultra'}
              data-testid="btn-ultra"
            >
              {loading && selectedTier === 'ultra' ? 'Processing...' : 'Get Ultra'}
            </Button>
          </div>
        </div>

        {/* Feature Comparison */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8">
          <h2 className="text-2xl font-bold mb-6 text-center">Compare Features</h2>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 font-semibold">Feature</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-600">Free</th>
                  <th className="text-center py-3 px-4 font-semibold text-cyan-600">Councillor Watch</th>
                  <th className="text-center py-3 px-4 font-semibold text-amber-600">Enhanced</th>
                  <th className="text-center py-3 px-4 font-semibold text-purple-600">Ultra</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr>
                  <td className="py-3 px-4">Articles per day</td>
                  <td className="py-3 px-4 text-center">3</td>
                  <td className="py-3 px-4 text-center text-slate-400">—</td>
                  <td className="py-3 px-4 text-center">15</td>
                  <td className="py-3 px-4 text-center font-bold">Unlimited</td>
                </tr>
                <tr className="bg-cyan-50/50">
                  <td className="py-3 px-4 font-medium">Councillor Watch AI</td>
                  <td className="py-3 px-4 text-center text-slate-400">✕</td>
                  <td className="py-3 px-4 text-center"><Check className="h-4 w-4 text-cyan-500 mx-auto" /></td>
                  <td className="py-3 px-4 text-center text-slate-400">✕</td>
                  <td className="py-3 px-4 text-center">
                    {billingCycle === 'yearly' ? (
                      <Check className="h-4 w-4 text-cyan-500 mx-auto" />
                    ) : (
                      <span className="text-slate-400">Yearly only</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="py-3 px-4">Interactive UK Map</td>
                  <td className="py-3 px-4 text-center">View only</td>
                  <td className="py-3 px-4 text-center text-slate-400">—</td>
                  <td className="py-3 px-4 text-center"><Check className="h-4 w-4 text-green-500 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="h-4 w-4 text-green-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-3 px-4">Find Your MP (Postcode)</td>
                  <td className="py-3 px-4 text-center text-slate-400">✕</td>
                  <td className="py-3 px-4 text-center text-slate-400">—</td>
                  <td className="py-3 px-4 text-center"><Check className="h-4 w-4 text-green-500 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="h-4 w-4 text-green-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-3 px-4">What If Seat Calculator</td>
                  <td className="py-3 px-4 text-center text-slate-400">✕</td>
                  <td className="py-3 px-4 text-center text-slate-400">—</td>
                  <td className="py-3 px-4 text-center"><Check className="h-4 w-4 text-green-500 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="h-4 w-4 text-green-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-3 px-4">Email Alerts</td>
                  <td className="py-3 px-4 text-center text-slate-400">✕</td>
                  <td className="py-3 px-4 text-center text-slate-400">—</td>
                  <td className="py-3 px-4 text-center">1 party</td>
                  <td className="py-3 px-4 text-center font-bold">All parties</td>
                </tr>
                <tr>
                  <td className="py-3 px-4">Local Council News</td>
                  <td className="py-3 px-4 text-center text-slate-400">✕</td>
                  <td className="py-3 px-4 text-center text-slate-400">—</td>
                  <td className="py-3 px-4 text-center text-slate-400">✕</td>
                  <td className="py-3 px-4 text-center"><Check className="h-4 w-4 text-green-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-3 px-4">API Access</td>
                  <td className="py-3 px-4 text-center text-slate-400">✕</td>
                  <td className="py-3 px-4 text-center text-slate-400">—</td>
                  <td className="py-3 px-4 text-center text-slate-400">✕</td>
                  <td className="py-3 px-4 text-center"><Check className="h-4 w-4 text-green-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-3 px-4">Ad-free</td>
                  <td className="py-3 px-4 text-center text-slate-400">✕</td>
                  <td className="py-3 px-4 text-center text-slate-400">—</td>
                  <td className="py-3 px-4 text-center"><Check className="h-4 w-4 text-green-500 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><Check className="h-4 w-4 text-green-500 mx-auto" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Trust badges */}
        <div className="text-center mt-8">
          <p className="text-sm text-slate-500">
            Secure payment via Stripe • Cancel anytime • 7-day money-back guarantee
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscribePage;
